/**
 * MashfaSidebar.tsx
 * ─────────────────────────────────────────────────────────────────
 * Vertical right-side sidebar (RTL — visually on the right).
 * Houses the main navigation links extracted from MashfaTopNav.
 * Style follows the same cream/sand pill design as the top nav.
 */

import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Home,
  Calendar,
  Users,
  Stethoscope,
  Clock,
  FileText,
  Mail,
  FlaskConical,
  ScanLine,
  ClipboardList,
  Scissors,
  Siren,
  MonitorDot,
  Building2,
  CreditCard,
  Banknote,
  LogOut,
  Menu,
  ChevronsLeft,
  ChevronsRight,
  X as XIcon,
  type LucideIcon,
  Landmark,
} from 'lucide-react'
import { useTodayAppointments } from '../../hooks/useDashboard'
import { useFeatureFlag } from '../../hooks/useFeatureFlags'
import { useHasPermission } from '../../hooks/usePermissions'
import { useUnreadCount } from '../../hooks/useMessaging'
import { useChatMute } from '../../hooks/useChatMute'
import { useToast } from '../ui/Toast'
import { useAuth } from '../../auth/AuthProvider'
import './MashfaSidebar.css'

/** Best-effort short, quiet beep (Web Audio — no asset). No-ops if the browser
 *  blocks audio (autoplay policy / no user gesture yet). */
function playChatBeep() {
  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioCtx) return
    const ctx = new AudioCtx()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.type = 'sine'
    osc.frequency.value = 660
    gain.gain.value = 0.04
    osc.start()
    osc.stop(ctx.currentTime + 0.14)
    osc.onended = () => { try { ctx.close() } catch { /* ignore */ } }
  } catch {
    /* ignore — sound is best-effort, the toast is the real notification */
  }
}

// ── Types ────────────────────────────────────────────────────────
type NavItem = {
  to: string
  label: string
  Icon: LucideIcon
  badge?: number
  /** When set, the item renders as a button that runs this instead of navigating. */
  onClick?: () => void
}

type NavGroup = {
  key: string
  title: string
  items: NavItem[]
}

// ── Component ────────────────────────────────────────────────────

export function MashfaSidebar() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { data: todayAppts } = useTodayAppointments()
  // Optional medical modules — shown only when licensed for the tenant.
  const labEnabled = useFeatureFlag('lab_enabled')
  const imagingEnabled = useFeatureFlag('imaging_enabled')
  const triageEnabled = useFeatureFlag('triage_enabled')
  const operationsEnabled = useFeatureFlag('operations_enabled')
  const emergencyEnabled = useFeatureFlag('emergency_enabled')
  const canViewFacility = useHasPermission('facility.view')
  // Cash-register grant. NOT billing.create: the Owner/manager role holds
  // billing.cash_register but not billing.create, and the owner must be able to
  // reach the till. See permission_catalog.py per-role default sets.
  const canUseCashier = useHasPermission('billing.cash_register')
  // D46 F1: the ledger is a licensed module (accounting_enabled) AND a permission
  // (accounting.view): the manager reads it by default, clinical roles do not.
  const accountingEnabled = useFeatureFlag('accounting_enabled')
  const canViewAccounting = useHasPermission('accounting.view')
  const { data: unread } = useUnreadCount()
  const { muted } = useChatMute()
  const { showToast } = useToast()
  const prevUnread = useRef<number | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  // Desktop collapse → narrow icon-rail; persisted across reloads.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('mashfa-sidebar-collapsed')
      return saved === null ? true : saved === '1'   // default: collapsed (closed)
    } catch { return true }
  })
  const toggleCollapsed = () => setCollapsed(v => {
    const next = !v
    try { localStorage.setItem('mashfa-sidebar-collapsed', next ? '1' : '0') } catch { /* ignore */ }
    return next
  })

  const todayCount = Array.isArray(todayAppts) ? todayAppts.length : 0

  // Active "new message" notification: when the unread total goes UP (a colleague
  // sent something) show a toast (+ a soft beep) — unless chat is muted. The
  // first read after mount only establishes the baseline (no toast on load).
  useEffect(() => {
    const count = unread?.unread_count ?? 0
    const prev = prevUnread.current
    prevUnread.current = count
    if (prev === null) return
    if (count > prev && !muted) {
      showToast('info', t('messaging.chat.newMessageToast', 'Neue Chat-Nachricht'))
      playChatBeep()
    }
  }, [unread?.unread_count, muted, showToast, t])

  const handleLogout = () => {
    logout()
    // Leave any tenant-scoped path before the next login (mirrors the topnav).
    navigate('/login', { replace: true })
  }

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  const isActive = (to: string) => {
    const base = to.split('?')[0]  // ignore query (e.g. /messaging?tab=chat)
    return base === '/' ? location.pathname === '/' : location.pathname.startsWith(base)
  }

  const groups: NavGroup[] = [
    {
      key: 'clinic',
      title: t('nav.klinik'),
      items: [
        { to: '/',             label: t('nav.dashboard'),    Icon: Home },
        { to: '/calendar',     label: t('nav.kalender'),     Icon: Calendar, badge: todayCount > 0 ? todayCount : undefined },
        { to: '/patients',     label: t('nav.patienten'),    Icon: Users },
        { to: '/doctors',      label: t('nav.aerzte'),       Icon: Stethoscope },
        ...(operationsEnabled ? [{ to: '/operations', label: t('nav.operations'), Icon: Scissors }] : []),
        ...(emergencyEnabled ? [{ to: '/emergency', label: t('nav.notaufnahme'), Icon: Siren }] : []),
        ...(emergencyEnabled ? [{ to: '/emergency/board', label: t('nav.notaufnahmeTafel'), Icon: MonitorDot }] : []),
        // Cashier is a standalone workstation, not an ER sub-page — hence its own
        // top-level entry rather than a nested item. The route still points at the ER
        // till because that is the only one built; the label and component stay
        // generic ('cashier') so a general front-desk till can adopt this same entry
        // later without a rename. Do not build that here.
        ...(emergencyEnabled && canUseCashier
          ? [{ to: '/emergency/cashier', label: t('nav.kasse'), Icon: Banknote }]
          : []),
        ...(canViewFacility ? [{ to: '/facility', label: t('nav.facility'), Icon: Building2 }] : []),
        { to: '/waiting-room', label: t('nav.wartezimmer'),  Icon: Clock },
      ],
    },
    {
      key: 'medicine',
      title: t('nav.medizin'),
      items: [
        { to: '/prescriptions',    label: t('nav.rezepte'),    Icon: FileText },
        { to: '/medical-reports',  label: t('nav.arztbriefe'), Icon: Mail },
        // Optional modules — visible when the tenant has them licensed.
        ...(labEnabled ? [{ to: '/lab', label: t('nav.lab'), Icon: FlaskConical }] : []),
        ...(imagingEnabled ? [{ to: '/imaging', label: t('nav.imaging'), Icon: ScanLine }] : []),
        ...(triageEnabled ? [{ to: '/triage', label: t('nav.triage'), Icon: ClipboardList }] : []),
        { to: '/billing', label: t('nav.abrechnung'), Icon: CreditCard },
        ...(accountingEnabled && canViewAccounting
          ? [{ to: '/accounting', label: t('nav.accounting'), Icon: Landmark }]
          : []),
      ],
    },
  ]

  return (
    <>
      {/* Mobile burger toggle (visible <900px) */}
      <button
        type="button"
        className="mashfa-sidebar-toggle"
        aria-label={mobileOpen ? t('header.closeMenu') : t('header.menu')}
        onClick={() => setMobileOpen(v => !v)}
      >
        {mobileOpen ? <XIcon size={20} /> : <Menu size={20} />}
      </button>

      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <div
          className="mashfa-sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`mashfa-sidebar${mobileOpen ? ' mobile-open' : ''}${collapsed ? ' collapsed' : ''}`}
        role="navigation"
        aria-label={t('sidebar.ariaLabel')}
        data-testid="main-sidebar"
      >
        <button
          type="button"
          className="sidebar-collapse"
          onClick={toggleCollapsed}
          aria-label={collapsed ? t('sidebar.expand', 'Ausklappen') : t('sidebar.collapse', 'Einklappen')}
        >
          {collapsed ? <ChevronsRight size={18} strokeWidth={1.8} /> : <ChevronsLeft size={18} strokeWidth={1.8} />}
          <span className="sidebar-label">{collapsed ? t('sidebar.expand', 'Ausklappen') : t('sidebar.collapse', 'Einklappen')}</span>
        </button>
        <div className="sidebar-scroll">
          {groups.map((group, gi) => (
            <div key={group.key} className="sidebar-group">
              {gi > 0 && <div className="sidebar-group-sep" aria-hidden="true" />}
              {group.title && <div className="sidebar-group-title">{group.title}</div>}
              <ul className="sidebar-group-items">
                {group.items.map(item => {
                  const active = !item.onClick && isActive(item.to)
                  const Icon = item.Icon
                  const inner = (
                    <>
                      <Icon size={19} strokeWidth={1.8} />
                      <span className="sidebar-label">{item.label}</span>
                      {item.badge !== undefined && (
                        <span className="sidebar-badge">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </>
                  )
                  return (
                    <li key={item.to}>
                      {item.onClick ? (
                        <button type="button" className="sidebar-link" onClick={item.onClick}>
                          {inner}
                        </button>
                      ) : (
                        <Link
                          to={item.to}
                          className={`sidebar-link${active ? ' active' : ''}`}
                          aria-current={active ? 'page' : undefined}
                        >
                          {inner}
                        </Link>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <button
            type="button"
            className="sidebar-logout"
            onClick={handleLogout}
            aria-label={t('topbar.logout')}
            data-testid="sidebar-logout-btn"
          >
            <LogOut size={18} strokeWidth={1.8} />
            <span className="sidebar-label">{t('topbar.logout')}</span>
          </button>
        </div>
      </aside>
    </>
  )
}

export default MashfaSidebar
